import { Chip, Stack, Typography } from '@mui/material';
import { FunctionComponent, useContext, useEffect, useState } from 'react'
import ReductionActionService from '../../../shared/services/reduction-action/reduction-action.service';
import { IAddActionToReportPeriod, IReductionAction } from '../../../shared/services/reduction-action/reduction-action.model';
import { useSearchParams } from 'react-router-dom';
import { ReportPeriodContext } from '../../../context/report-periods/report-periods';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import { deleteQueryParamFromUrl } from '../../../shared/utils/misc-shared-utils';
import { useIsReportPeriodLocked } from '../../../hooks/use-is-report-period-locked';
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper';
import { ReductionActionContentSummary } from '../../presentation-components/reduction-components/reduction-action-content-summary';

// A library action's detail: group, description and what the action says about itself (typical
// saving, cost, source). Adds the action to the plan for the selected open period; the plan
// dialog is where the saving and cost are entered afterwards.
export const ReductionActionLibraryDialog: FunctionComponent<{
  open: boolean,
  closeHandler: () => void,
  getAllActionsForOrganisation: () => void,
}> = ({
  open,
  closeHandler,
  getAllActionsForOrganisation,
}) => {
    const [searchParams] = useSearchParams()
    //Services
    const reductionActionService = new ReductionActionService()
    const reportPeriodContext = useContext(ReportPeriodContext)
    const userNotificationsContext = useContext(UserNotificationsContext)
    //Hooks
    const isReportPeriodLocked = useIsReportPeriodLocked()
    //State
    const [reductionAction, setReductionAction] = useState<IReductionAction>()
    const [postReductionAction, setPostReductionAction] = useState<IAddActionToReportPeriod>({
      reductionActionId: '',
      reportPeriodId: '',
    })
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

    useEffect(() => {
      if (searchParams.has('actionId')) {
        const token = searchParams.get('actionId')
        if (token) {
          reductionActionService.getReductionAction(token)
            .then(response => {
              setReductionAction(response)
              setPostReductionAction(prevState => ({
                ...prevState,
                reductionActionId: token
              }));
            })
            .catch(err => console.log(err))
        }
        deleteQueryParamFromUrl(searchParams, 'actionId')
      }
    }, [searchParams])

    // Adds go to the selected OPEN period, matching the library card.
    useEffect(() => {
      if (reportPeriodContext.lastSelectedOpenReportId) {
        setPostReductionAction(prevState => ({
          ...prevState,
          reportPeriodId: reportPeriodContext.lastSelectedOpenReportId || ''
        }));
      }
    }, [reportPeriodContext.lastSelectedOpenReportId])

    const successHandler = () => {
      if (isReportPeriodLocked) {
        userNotificationsContext.openDialog({ active: true, title: 'Action Not Allowed', message: `The selected report period is locked and cannot be modified`, buttonCopy: 'Ok', hideCancelButton: true })
      } else {
        if (postReductionAction.reductionActionId && postReductionAction.reportPeriodId && !isSubmitting) {
          setIsSubmitting(true)
          reductionActionService.addReductionActionToReportPeriod(postReductionAction)
            .then(() => {
              getAllActionsForOrganisation();
              userNotificationsContext.openSnackbar({ open: true, message: 'Added successfully' })
              closeHandler();
              setIsSubmitting(false)
            })
            .catch(err => {
              if (err?.response?.data?.code === 1) {
                userNotificationsContext.openSnackbar({ open: true, message: 'Report period is locked', backgroundColor: 'text.primary' });
              } else {
                userNotificationsContext.openSnackbar({ open: true, message: err?.response?.data?.message || 'There was an error, please try again', backgroundColor: 'text.primary' });
              }
              setIsSubmitting(false)
              console.log(err)
            })
        }
      }
    }

    return (
      <DialogWrapper
        open={open}
        onClose={closeHandler}
        title={reductionAction?.name ?? 'Reduction action'}
        cancelHandler={closeHandler}
        submitHandler={successHandler}
        isSubmitting={isSubmitting}
        submitButtonText='Add to Plan'
        submitButtonLoadingText='Adding to Plan'
        disableSaveButton={isReportPeriodLocked === true || !reductionAction}
        disableCancelButton={false}
        maxWidth='sm'
      >
        {reductionAction && (
          <>
            <Stack display='flex' direction='row' justifyContent='space-between' alignItems='center' sx={{ width: '100%', mb: 1 }}>
              <Typography sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>{reductionAction.group?.title}</Typography>
              {reductionAction.isCustom && <Chip label='Custom' size='small' />}
            </Stack>
            <Typography sx={{ color: 'text.secondary', mb: 2 }}>{reductionAction.description}</Typography>
            <ReductionActionContentSummary action={reductionAction} variant='full' />
          </>
        )}
      </DialogWrapper>
    )
  }
